import React, { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { COUNTIES, LOCALITIES } from "@/data/romania";

// diacritic-insensitive match so "bucuresti" finds "București"
const strip = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const diacriticFilter = (value, search) => (strip(value).includes(strip(search)) ? 1 : 0);
const roSort = (arr) => [...arr].sort((a, b) => a.localeCompare(b, "ro"));
const SORTED_COUNTIES = roSort(COUNTIES);

function Combo({ value, onChange, options, placeholder, searchPlaceholder, emptyText, disabled, testId, allOption }) {
  const [open, setOpen] = useState(false);
  const label = value || (allOption && value === "" ? allOption.label : "");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className="amd-input h-11 w-full flex items-center justify-between gap-2 px-3 text-left disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="truncate" style={{ color: label ? "var(--amd-text)" : "var(--amd-text-muted)" }}>
            {label || placeholder}
          </span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <Command filter={diacriticFilter}>
          <CommandInput placeholder={searchPlaceholder} data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList className="max-h-64">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {allOption && (
                <CommandItem value={allOption.label} onSelect={() => { onChange(""); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === "" ? "opacity-100" : "opacity-0"}`} />
                  {allOption.label}
                </CommandItem>
              )}
              {options.map((o) => (
                <CommandItem key={o} value={o} onSelect={() => { onChange(o); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === o ? "opacity-100" : "opacity-0"}`} />
                  {o}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Cascading county (județ) -> locality (localitate) picker.
// onChange(nextCounty, nextCity) is called on every change.
export default function LocationPicker({
  county = "",
  city = "",
  onChange,
  searchMode = false,
  testIdPrefix = "loc",
  className = "grid grid-cols-1 sm:grid-cols-2 gap-3",
}) {
  const localities = county ? roSort(LOCALITIES[county] || []) : [];
  return (
    <div className={className}>
      <Combo
        value={county}
        onChange={(c) => onChange(c, "")}
        options={SORTED_COUNTIES}
        placeholder="Alege județul"
        searchPlaceholder="Caută județ…"
        emptyText="Niciun județ găsit"
        testId={`${testIdPrefix}-county`}
        allOption={searchMode ? { label: "Toată țara" } : null}
      />
      <Combo
        value={city}
        onChange={(c) => onChange(county, c)}
        options={localities}
        placeholder={county ? "Alege localitatea" : "Alege întâi județul"}
        searchPlaceholder="Caută localitatea…"
        emptyText="Nicio localitate găsită"
        disabled={!county}
        testId={`${testIdPrefix}-city`}
        allOption={searchMode && county ? { label: "Tot județul" } : null}
      />
    </div>
  );
}
